<template>
  <div
    class="f-relative order-bump-space"
    :class="`fapi-order-bump-product-${orderBump.formItem.code}`"
  >
    <BaseAlignTopCheckbox
      :id="uuid"
      v-model="value"
      :disabled="orderBump.formItem.soldOut"
      class="order-bump-label f-rounded-lg f-p-3 f-mb-3"
      @input="orderBumpChanged"
    >
      <svg
        class="order-bump-arrow f-w-12 f-absolute f-top-0 f-left-0 f--ml-14 f--mt-4 f-rotate-12 f-transform svg-inline--fa fa-share fa-w-16"
        focusable="false"
        data-prefix="fas"
        data-icon="share"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      >
        <path
          fill="currentColor"
          d="M503.691 189.836L327.687 37.851C312.281 24.546 288 35.347 288 56.015v80.053C127.371 137.907 0 170.1 0 322.326c0 61.441 39.581 122.309 83.333 154.132 13.653 9.931 33.111-2.533 28.077-18.631C66.066 312.814 132.917 274.316 288 272.085V360c0 20.7 24.3 31.453 39.687 18.164l176.004-152c11.071-9.562 11.086-26.753 0-36.328z"
        ></path>
      </svg>
      <span
        :class="{ 'f-line-through': orderBump.formItem.soldOut }"
        v-html="name"
      ></span>
    </BaseAlignTopCheckbox>
    <div
      :class="{ 'f-px-3': true, 'f-line-through': orderBump.formItem.soldOut }"
      v-if="description"
      v-html="description"
    ></div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import FormOrderBump from "@/module/FormOrderBump";
import SelectedItem from "@/module/Form/SelectedItem";
import BaseAlignTopCheckbox from "@/components/Form/Basic/Inputs/BaseAlignTopCheckbox.vue";
import UrlParser from "@/module/Utils/UrlParser";
import FormResource from "@/module/Form/Resources/FormResource";

export default Vue.extend({
  name: "OrderBump",
  components: { BaseAlignTopCheckbox },
  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    orderBump: {
      type: Object as PropType<FormOrderBump>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    showPricesIncludingVat: {
      type: Boolean,
      required: true,
    },
  },

  data(): { value: boolean } {
    return {
      value: false,
    };
  },

  computed: {
    uuid(): string {
      return this.$id("order-bump-checkbox-" + this.orderBump.id);
    },

    name(): string {
      return this.orderBump.name
        .replace(
          /{price}/g,
          this.$n(this.finalPrice, {
            style: "currency",
            currency: this.currency,
            minimumFractionDigits:
              this.formResource.styling?.minimum_fraction_digits ?? 0,
            maximumFractionDigits:
              this.formResource.styling?.maximum_fraction_digits ?? 2,
          }),
        )
        .replace(/{itemName}/g, this.orderBump.formItem.name)
        .replace(
          /{priceBeforeDiscount}/g,
          this.$n(this.originalPrice, {
            style: "currency",
            currency: this.currency,
            minimumFractionDigits:
              this.formResource.styling?.minimum_fraction_digits ?? 0,
            maximumFractionDigits:
              this.formResource.styling?.maximum_fraction_digits ?? 2,
          }),
        )
        .replace(/{image}/g, this.image);
    },

    description(): string | null {
      if (this.orderBump.description === null) {
        return null;
      }
      return this.orderBump.description
        .replace(
          /{price}/g,
          this.$n(this.finalPrice, {
            style: "currency",
            currency: this.currency,
          }),
        )
        .replace(/{itemName}/g, this.orderBump.formItem.name)
        .replace(
          /{priceBeforeDiscount}/g,
          this.$n(this.originalPrice, {
            style: "currency",
            currency: this.currency,
          }),
        )
        .replace(/{image}/g, this.image);
    },

    finalPrice(): number {
      if (this.orderBump.formItem.currentDiscountedPrice !== null) {
        return this.discountedPrice;
      } else {
        return this.currentPrice;
      }
    },

    currentPrice(): number {
      if (this.showPricesIncludingVat) {
        return this.orderBump.formItem.currentPriceIncVat;
      } else {
        return this.orderBump.formItem.currentPrice;
      }
    },

    originalPrice(): number {
      if (this.showPricesIncludingVat) {
        return this.orderBump.formItem.originalPriceIncVat;
      } else {
        return this.orderBump.formItem.originalPrice;
      }
    },

    image(): string {
      const url = this.orderBump.formItem.imageUrl;

      if (url === null) {
        return "";
      }

      return "<img src='" + url + "' class='fapi-order-bump-image'>";
    },

    discountedPrice(): number {
      if (this.showPricesIncludingVat) {
        return Number(this.orderBump.formItem.currentDiscountedPriceIncVat);
      } else {
        return Number(this.orderBump.formItem.currentDiscountedPrice);
      }
    },
  },

  mounted(): void {
    const urlParser = UrlParser.getInstance();
    const value =
      urlParser.getParamFromFormData(
        "fapi-form-item-" + this.orderBump.formItem.code,
      ) || urlParser.getParam("fapi-form-item-" + this.orderBump.formItem.code);

    if (value !== null && value !== "") {
      this.value = value !== "0";
      this.orderBumpChanged(this.value);
    }
  },

  methods: {
    orderBumpChanged(value: boolean): void {
      this.$emit("orderBumpChanged", {
        id: this.orderBump.formItem.id,
        selected: value,
        count: 1,
      } as SelectedItem);
    },
  },
});
</script>

<style lang="scss">
.fapi-order-form {
  .order-bump-label {
    background-color: var(--order-bump-container-content-item-background-color);

    p {
      margin-bottom: 0;
    }

    .order-bump-arrow {
      display: var(--order-bump-item-arrow-display);
      color: var(--order-bump-item-arrow-color);
    }
  }

  .order-bump-space {
    margin: var(--order-bump-space-margin);
  }

  .fapi-order-bump-image {
    max-width: 250px;
    max-height: 150px;
    border-radius: 5px;
  }
}
</style>
